import { useEffect } from "react";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  underwritingAgentReviewQueryKey,
  underwritingAgentStatusQueryKey,
  underwritingFindingsApi,
  underwritingFindingsQueryKey
} from "api/underwriting-agents/underwriting-findings";
import { getErrorMessage } from "utils/error";
import type {
  UnderwritingAgentStatus,
  UnderwritingReportKey
} from "api/underwriting-agents/types";
import { useUnderwritingAgentsEnabled } from "../underwritingAgentsFlag";
import { isReviewInProgress } from "./reviewStatus";
import {
  getRerunRequests,
  recordRerunRequest,
  useRerunRequests,
  withPendingReruns
} from "./rerunRequests";

// While the agent is still working, its status and findings are polled so they show up without a reload.
const POLL_WHILE_RUNNING_MS = 10_000;

const pollWhileWorking = (
  applicationId: string,
  status: UnderwritingAgentStatus | undefined
) =>
  status &&
  isReviewInProgress(
    withPendingReruns(status, applicationId, getRerunRequests())
  )
    ? POLL_WHILE_RUNNING_MS
    : false;

/** Toasts a failed load once; several components read the same query, so the toast id keeps it to one. */
const useErrorToast = (error: Error | null, toastId: string) => {
  useEffect(() => {
    if (error) toast.error(getErrorMessage(error), { toastId });
  }, [error, toastId]);
};

/**
 * The agent's status on the application; undefined while loading, when the partner has no underwriting agents,
 * or when the user can't view reports (findings quote them).
 */
export const useUnderwritingAgentStatus = (
  applicationId: string,
  { canViewReports }: { canViewReports: boolean }
) => {
  const featureEnabled = useUnderwritingAgentsEnabled();
  const rerunRequests = useRerunRequests();

  const { data, error } = useQuery({
    queryKey: underwritingAgentStatusQueryKey(applicationId),
    queryFn: () => underwritingFindingsApi.getStatus(applicationId),
    enabled: featureEnabled && canViewReports && Boolean(applicationId),
    refetchInterval: (query) =>
      pollWhileWorking(applicationId, query.state.data)
  });
  useErrorToast(error, "underwriting-agent-status");

  if (!canViewReports || !data?.enabled) return undefined;

  return withPendingReruns(data, applicationId, rerunRequests);
};

export const useUnderwritingFindings = (
  applicationId: string,
  { enabled }: { enabled: boolean }
) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: underwritingFindingsQueryKey(applicationId),
    queryFn: () => underwritingFindingsApi.list(applicationId),
    enabled: enabled && Boolean(applicationId),
    refetchInterval: () =>
      pollWhileWorking(
        applicationId,
        queryClient.getQueryData<UnderwritingAgentStatus>(
          underwritingAgentStatusQueryKey(applicationId)
        )
      )
  });
  useErrorToast(query.error, "underwriting-agent-findings");

  return query;
};

/** Refetches the agent's status and findings on the application. */
const refreshReview = (queryClient: QueryClient, applicationId: string) =>
  queryClient.invalidateQueries({
    queryKey: underwritingAgentReviewQueryKey(applicationId)
  });

export const useReviewUnderwritingFindings = (applicationId: string) => {
  const queryClient = useQueryClient();
  const onSuccess = () => refreshReview(queryClient, applicationId);
  const onError = (err: Error) => toast.error(getErrorMessage(err));

  const reviewOne = useMutation({
    mutationKey: ["UNDERWRITING_FINDING", applicationId, "UPDATE"],
    mutationFn: (findingId: number) =>
      underwritingFindingsApi.review({ applicationId, findingId }),
    onSuccess,
    onError
  });

  const undoOne = useMutation({
    mutationKey: ["UNDERWRITING_FINDING", applicationId, "DELETE"],
    mutationFn: (findingId: number) =>
      underwritingFindingsApi.undoReview({ applicationId, findingId }),
    onSuccess,
    onError
  });

  const reviewAll = useMutation({
    mutationKey: ["UNDERWRITING_FINDINGS", applicationId, "UPDATE"],
    mutationFn: (applicantId: number) =>
      underwritingFindingsApi.reviewAll({
        applicationId,
        applicant_id: applicantId
      }),
    onSuccess,
    onError
  });

  const isPending = (findingId: number) =>
    (reviewOne.isPending && reviewOne.variables === findingId) ||
    (undoOne.isPending && undoOne.variables === findingId);

  return { reviewOne, undoOne, reviewAll, isPending };
};

/** Try again on a report the agent couldn't review: it reads as running until the agent's new check is done. */
export const useRerunUnderwritingCheck = (applicationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["UNDERWRITING_AGENT_RERUN", applicationId, "CREATE"],
    mutationFn: ({
      applicantId,
      reportKey
    }: {
      applicantId: number;
      reportKey: UnderwritingReportKey;
    }) =>
      underwritingFindingsApi.rerun({ applicationId, applicantId, reportKey }),
    onSuccess: (_, { applicantId, reportKey }) => {
      const failedCheck = queryClient
        .getQueryData<UnderwritingAgentStatus>(
          underwritingAgentStatusQueryKey(applicationId)
        )
        ?.reports.find(
          (report) =>
            report.applicant_id === applicantId &&
            report.report_key === reportKey
        );
      recordRerunRequest(
        applicationId,
        applicantId,
        reportKey,
        failedCheck?.finished_at ?? null
      );
      refreshReview(queryClient, applicationId);
    },
    // A 422 means the report changed meanwhile (already rerun, or the application was decided): show where it is now.
    onError: (err: Error) => {
      toast.error(getErrorMessage(err));
      refreshReview(queryClient, applicationId);
    }
  });
};
